import React, { useContext, useEffect, useMemo, useState } from 'react'
import { getPrice, translate } from '../utils/translator'
import { useParams } from 'react-router-dom'
import { useCartContext } from '../customHooks/useCart'
import { ShippingDataContext } from '../pages/Shipping/Shipping'
import { useShippingPageContext } from '../pages/v2/ShippingV2/providers/ShippingPageProvider'
import { ImSpinner3 } from "react-icons/im";
import PortaledModal from './Modals/PortaledModal';
import useIsMobile from '../customHooks/useIsMobile';
import { trackOncePerSession } from '../PixelTrackers/trackingUtils';
import { toChargeCurrency } from '../utils/helpers';
import { ORDER_FLOWS } from '../utils/consts'
import DisclaimerTooltip from './DisclaimerTooltip';
import { TOOLTIP_SHIPPING_FEE } from '../utils/consts';
import ServiceFeeTestHelper from './ServiceFeeTestHelper';
import { carePlusPlanRowLabel } from '../pages/CarePlusShared/carePlusPlanLabel';

type Props = {
    setRxProductFinalPrice?: (price: number) => void
    shippingFee?: number
    isExpressShipping?: boolean
    isMobileModal?: boolean
    setTotalPrice?: (price: number) => void
}

const CANNABIS_ID_TITLE = "Digitaler Cannabis-Ausweis";
const CartViewBreakdown: React.FC<Props> = ({ setRxProductFinalPrice, shippingFee, isExpressShipping=false, setTotalPrice=undefined }) => {

    const { language } = useParams()
    const { getCart, cartCurrency } = useCartContext();
    const cart = getCart();
    const cartCountry = cart.locale_id?.split('-')[1].toLowerCase() || 'de'
    const { cannabisIDState } = useContext(ShippingDataContext) || {}; // Shipping page
    const { expressDeliveryState } = useShippingPageContext();
    const expressDeliveryPrice = expressDeliveryState?.deliveryPrice || 0;
    const deliveryFeeIsLoading = expressDeliveryState?.deliveryPriceLoading;
    const cannbisIdAsOrderProduct = cart.products.find(product => (product.type === "cannabis_id" || product.title === CANNABIS_ID_TITLE));
    const videoConsultationAsOrderProduct = cart.products.find(product => product.type === "online_vd"); // There is no other way to get this without breaking some types
    const isSchengenFlow = cart.flow === ORDER_FLOWS.SCHENGEN;

    const isMobile = useIsMobile();
    const [animationState, setAnimationState] = useState<string>("");
    const [showCostDetails, setShowCostDetails] = useState(false);
    const [openTooltip, setOpenTooltip] = useState<string | null>(null);

    // Handle animation state for mobile modal
    useEffect(() => {
        if (!animationState){
          setAnimationState("hidden");
        } else if (showCostDetails && isMobile) {
          setAnimationState("animate-fade-in-up");
        } else if (isMobile) {
          setAnimationState("animate-fade-out-down");
        }
    }, [showCostDetails]);


    const handleClose = () => {
        if (isMobile) {
            setAnimationState("animate-fade-out-down");
            setTimeout(() => {
                setAnimationState("hidden");
                setShowCostDetails(false);
            }, 500);
        }
        else {
            setShowCostDetails(false);
        }
    }

    // calculate custom shipping fee value
    const actualShippingFee = useMemo(() => {
        if (isExpressShipping && expressDeliveryPrice > 0) {
            return expressDeliveryPrice;
        }
        return cart.only_prescription ? 0 : cart.shipping_fee || shippingFee || 0;
    }, [isExpressShipping, expressDeliveryPrice, shippingFee, cart.shipping_fee]);

    
    const summaryRows = useMemo(() => {
        const totalDrugsPrice = cart.only_prescription ? 0 : cart.drugs_price || 0;
        const totalOTCPrice = cart.otc_price || 0;
        const totalServiceFee = cart.only_prescription && !isSchengenFlow ? 0 : cart.service_fee || 0;
        // Care+ (HL-9781): the plan fee already covers treatment + service, so hide those two
        // rows here — they're shown as part of the Care+ Plan line, not as separate charges.
        const isCarePlusOrder = Number(cart.care_plus_plan_fee ?? 0) > 0;

        return {
            treatment_fee: {
                shouldShow: cart.treatment_fee === 0 || isCarePlusOrder ? false : true,
                value: cart.treatment_fee || 0, //Treatment fee defined as the maximum value of the treatment fee of all products in the cart
            },

            // Care+ platform fee — plan_fee × period_months, prepaid for the period.
            care_plus_plan_fee: {
                shouldShow: Number(cart.care_plus_plan_fee ?? 0) > 0,
                value: Number(cart.care_plus_plan_fee ?? 0),
            },

            consultation_fee: {
                shouldShow: cart.consultation_fee === 0 ? false : true,
                value: cart.consultation_fee || 0,
            },

            total_drugs: {
                shouldShow: Boolean(!cart.only_prescription) && totalDrugsPrice > 0,
                value: cart.only_prescription ? 0 : totalDrugsPrice
            },

            total_otc_drugs: {
                shouldShow: totalOTCPrice > 0,
                value: totalOTCPrice
            },

            cannabis_id: {
                shouldShow: Boolean(cannbisIdAsOrderProduct) || Boolean(cannabisIDState?.isCannabisIDAddedToOrder),
                value: cart.only_prescription ? (cannbisIdAsOrderProduct?.item_price ? cannbisIdAsOrderProduct.item_price : 0) : 0
            },

            shipping_fee: {
                shouldShow: videoConsultationAsOrderProduct || (Boolean(cart.only_prescription) && totalOTCPrice === 0) ? false : true,
                value: 0,
            },

            service_fee: {
                shouldShow: !isCarePlusOrder,
                value: totalServiceFee,
            },

            coupon: {
                shouldShow: Boolean(cart.bonus_value) && !Boolean(cart.applied_subscription_discount) && Boolean(cart.only_prescription ? !cart.auto_bonus : true), // Auto bonus should not be applied on only prescription
                value: (-1) * Number(cart.bonus_value),
            },

            subscription_discount: {
                shouldShow: Boolean(cart.applied_subscription_discount),
                value: (-1) * Number(cart.applied_subscription_discount),
            }
        }

    },[cart, cart.locale_id, cart.products, cannabisIDState?.isCannabisIDAddedToOrder, cannabisIDState?.cost, actualShippingFee]);


    const shippingFeeRowElement = useMemo(() => {
        let text;

        if (cart.self_pickup_pharmacy_id ) {
            text = translate(language, "default", "OrderWindow/self_pickup");
        }

        // handle shipping text
        else if (cart.only_prescription && summaryRows.total_otc_drugs.value === 0) { //TODO: check if it's subsidized (mainly for FR catalogs) and if so, we need to show the shipping fee
            // text = translate(language, "default", "OrderWindow/prescription_delivery");
            // value = DISCOUNTED_SHIPPING_FEE
            return { text: "", price: "", isLoading: false }

        } else if (isExpressShipping) {
            text = translate(language, "default", "OrderWindow/express_delivery");
        } else {
            text = translate(language, "default", "OrderWindow/next_day_delivery");
        }

        
        if (deliveryFeeIsLoading && isExpressShipping) {
            return { text: "", price: "", isLoading: true }
        }

        return { 
            text: text, 
            price: getPrice(actualShippingFee, cartCurrency.locale, cartCurrency.rate),
            isLoading: false
        }

    }, [cart, cartCurrency, isExpressShipping, actualShippingFee, deliveryFeeIsLoading])

    useEffect(() => {
    //TODO: This is backwards compatibility for old shipping, remove this once Shipping.js is deprecated
        if (setRxProductFinalPrice){ 
            setRxProductFinalPrice(summaryRows.total_drugs.value + summaryRows.treatment_fee.value + summaryRows.subscription_discount.value + summaryRows.coupon.value);
        }
        if (setTotalPrice){
            setTotalPrice(cartTotalPrice);
        }
    }, [summaryRows])

    const cartTotalPrice = useMemo(() => {
        //Sum the total of summaryRows
        let sum = 0;

        Object.entries(summaryRows).forEach(([field, summaryRow]) => {
            if (summaryRow.shouldShow) {
                sum += summaryRow.value;
            }
        })

        sum += actualShippingFee;

        return sum

    }, [cart, language, summaryRows, actualShippingFee]);

    const renderSummaryContent = () => (
        <>
            {/* Drugs Price  */}
            {
                summaryRows["total_drugs"].shouldShow &&
                <div>
                    <div id="cart-view-breakdown-row-total-drugs" className="flex justify-between items-start px-5 py-3">
                        <div className="flex-1">
                            <h4 id="cart-view-breakdown-row-text-total-drugs" className="font-roboto text-[15px] font-bold text-[#0D2C54] mb-1">
                                {translate(language, "default", "OrderWindow/drug_price")}
                            </h4>
                            <div className="flex items-center">
                                <span className="text-[#656D79] text-[13px]">
                                    {translate(language, "default", "OrderWindow/drug_price_info")}
                                </span>
                            </div>
                        </div>
                        <div className="ml-4">
                            <span id='cart-view-breakdown-row-value-total-drugs' className="font-roboto text-[15px] font-medium text-[#656D79]" translate="no">
                                {getPrice(cart.drugs_price, cartCurrency.locale, cartCurrency.rate)}
                            </span>
                        </div>
                    </div>
                    <hr style={{border: '1px dotted #E5E5E5', width: '90%', margin: '0 auto'}} />
                </div>
            }

            {/* OTC Drugs Price  */}
            {
                summaryRows["total_otc_drugs"].shouldShow &&
                <div>
                    <div id="cart-view-breakdown-row-total-otc-drugs" className="flex justify-between items-start px-5 py-3">
                        <div className="flex-1">
                            <h4 id="cart-view-breakdown-row-text-total-otc-drugs" className="font-roboto text-[15px] font-bold text-[#0D2C54] mb-1">
                                {translate(language, "default", "OrderSummary/otc_addons_price")}
                            </h4>
                            <div className="flex items-center">
                                <span className="text-[#656D79] text-[13px]">
                                    {translate(language, "default", "OrderSummary/otc_addons_price_info")}
                                </span>
                            </div>
                        </div>
                        <div className="ml-4">
                            <span id='cart-view-breakdown-row-value-total-otc-drugs' className="font-roboto text-[15px] font-medium text-[#656D79]" translate="no">
                                {getPrice(summaryRows["total_otc_drugs"].value, cartCurrency.locale, cartCurrency.rate)}
                            </span>
                        </div>
                    </div>
                    <hr style={{border: '1px dotted #E5E5E5', width: '90%', margin: '0 auto'}} />
                </div>
            }

            {/* Treatment Fee  */}
            {
                summaryRows["treatment_fee"].shouldShow &&
                <div>
                    <div id="cart-view-breakdown-row-treatment-fee" className="flex justify-between items-start px-5 py-3">
                        <div className="flex-1">
                            <h4 id="cart-view-breakdown-row-text-treatment-fee" className="font-roboto text-[15px] font-bold text-[#0D2C54] mb-1">
                                {translate(language, "default", "OrderWindow/treatment_fee")}
                            </h4>
                            <div className="flex items-center">
                                <span className="text-[#656D79] text-[13px]">
                                    {translate(language, "default", `OrderWindow/treatment_fee_info_${cartCountry}`)}
                                </span>
                            </div>
                        </div>
                        <div className="ml-4">
                            <span id='cart-view-breakdown-row-value-treatment-fee' className="font-roboto text-[15px] font-medium text-[#656D79]" translate="no">
                                {getPrice(summaryRows["treatment_fee"].value, cartCurrency.locale, cartCurrency.rate)}
                            </span>
                        </div>
                    </div>
                    <hr style={{border: '1px dotted #E5E5E5', width: '90%', margin: '0 auto'}} />
                </div>
            }

            {/* Care+ Plan Fee — label follows the selected tier (Care+ / Care+ Pro / Care+ Max). */}
            {
                summaryRows["care_plus_plan_fee"].shouldShow &&
                <div>
                    <div id="cart-view-breakdown-row-care-plus-plan-fee" className="flex justify-between items-start px-5 py-3">
                        <div className="flex-1">
                            <h4 id="cart-view-breakdown-row-text-care-plus-plan-fee" className="font-roboto text-[15px] font-bold text-[#0D2C54] mb-1">
                                {carePlusPlanRowLabel(cart.care_plus_plan_tier, language, translate)}
                            </h4>
                        </div>
                        <div className="ml-4">
                            <span id='cart-view-breakdown-row-value-care-plus-plan-fee' className="font-roboto text-[15px] font-medium text-[#656D79]">
                                {getPrice(summaryRows["care_plus_plan_fee"].value, cartCurrency.locale, cartCurrency.rate)}
                            </span>
                        </div>
                    </div>
                    <hr style={{border: '1px dotted #E5E5E5', width: '90%', margin: '0 auto'}} />
                </div>
            }

            {/* Consultation Fee  */}
            {
                summaryRows["consultation_fee"].shouldShow &&
                <div>
                    <div id="cart-view-breakdown-row-consultation-fee" className="flex justify-between items-start px-5 py-3">
                        <div className="flex-1">
                            <h4 id="cart-view-breakdown-row-text-consultation-fee" className="font-roboto text-[15px] font-bold text-[#0D2C54] mb-1">
                                {translate(language, "default", cart.is_follow_up_consultation ? "OrderWindow/consultation_fee_follow_up" : "OrderWindow/consultation_fee")}
                            </h4>
                            <div className="flex items-center">
                                <span className="text-[#656D79] text-[13px]">
                                    {translate(language, "default", `OrderWindow/consultation_fee_info_${cartCountry}`)}
                                </span>
                            </div>
                        </div>
                        <div className="ml-4">
                            <span id='cart-view-breakdown-row-value-consultation-fee' className="font-roboto text-[15px] font-medium text-[#656D79]" translate="no">
                                {getPrice(summaryRows["consultation_fee"].value, cartCurrency.locale, cartCurrency.rate)}
                            </span>
                        </div>
                    </div>
                    <hr style={{border: '1px dotted #E5E5E5', width: '90%', margin: '0 auto'}} />
                </div>
            }


            {/* Service Fee  */}
            {
                summaryRows["service_fee"].shouldShow &&
                <div>
                    <div id="cart-view-breakdown-row-service-fee" className="flex justify-between items-start px-5 py-3">
                        <div className="flex-1">
                            <h4 id="cart-view-breakdown-row-text-service-fee" className="font-roboto text-[15px] font-bold text-[#0D2C54] mb-1">
                                {translate(language, "default", "OrderWindow/service_fee")}
                            </h4>
                            <div className="flex items-center">
                                <span className="text-[#656D79] text-[13px]">
                                    {translate(language, "default", `OrderWindow/service_fee_info_${cartCountry}`)}
                                </span>
                            </div>
                            <ServiceFeeTestHelper
                                variant={cart.service_fee_test_variant}
                                flowerCount={cart.service_fee_test_flower_count ?? 0}
                                nonFlowerPresent={cart.service_fee_test_non_flower_present ?? false}
                                language={language}
                                id="cart-view-breakdown-service-fee-test-helper"
                                className="text-[#656D79] text-[12px] mt-1 whitespace-nowrap"
                            />
                        </div>
                        <div className="ml-4">
                            <span id='cart-view-breakdown-row-value-service-fee' className="font-roboto text-[15px] font-medium text-[#656D79]" translate="no">
                                {getPrice(summaryRows["service_fee"].value, cartCurrency.locale, cartCurrency.rate)}
                            </span>
                        </div>
                    </div>
                    <hr style={{border: '1px dotted #E5E5E5', width: '90%', margin: '0 auto'}} />
                </div>
            }

            {/* Shipping Fee  */}
            {summaryRows["shipping_fee"].shouldShow && shippingFeeRowElement && (actualShippingFee > 0 || Boolean(shippingFeeRowElement.text)) && 
                <div>
                    {shippingFeeRowElement.isLoading ? (
                        <div id="cart-view-breakdown-row-shipping-fee-loading" className="flex justify-center items-center px-5 py-3">
                            <ImSpinner3 className="animate-spin text-2xl" />
                        </div>
                    ) : (
                        <div id="cart-view-breakdown-row-shipping-fee" className="flex justify-between items-start px-5 py-3">
                            <div className="flex-1">
                                <h4 id="cart-view-breakdown-row-text-shipping-fee" className="font-roboto text-[15px] font-bold text-[#0D2C54] mb-1">
                                    {translate(language, "default", "OrderWindow/shipping_fee")}
                                </h4>
                                <div className="flex items-center gap-2">
                                    <span className="text-[#656D79] text-[13px]">
                                        {shippingFeeRowElement.text}
                                    </span>
                                    {Boolean(isExpressShipping) && (
                                        <DisclaimerTooltip
                                            isOpen={openTooltip === TOOLTIP_SHIPPING_FEE}
                                            onToggle={() => setOpenTooltip(openTooltip === TOOLTIP_SHIPPING_FEE ? null : TOOLTIP_SHIPPING_FEE)}
                                            tooltipText={(() => {
                                            const doctorApprovalFee = cart.fast_approval_fee ?? 0;
                                            const customShipping = cart.custom_shipping_fee ?? 0;
                                            const deliveryFee = expressDeliveryPrice > 0
                                                ? Math.max(0, expressDeliveryPrice - doctorApprovalFee)
                                                : (doctorApprovalFee > 0 && customShipping >= 10
                                                    ? Math.max(0, customShipping - doctorApprovalFee)
                                                    : customShipping);
                                            return translate(language, "default", "OrderWindow/express_delivery_price_tooltip")
                                            .replace(':deliveryFee', getPrice(deliveryFee, cartCurrency.locale, cartCurrency.rate))
                                            .replace(':doctorApprovalFee', getPrice(doctorApprovalFee, cartCurrency.locale, cartCurrency.rate));
                                        })()}
                                            tooltipClassName="min-w-[280px] sm:min-w-[350px] md:min-w-[420px]"
                                        />
                                    )}
                                </div>
                            </div>
                            <div className="ml-4">
                                <span id='cart-view-breakdown-row-value' className={`font-roboto text-[15px] ${actualShippingFee > 0 ? 'font-medium text-[#656D79]' : 'font-bold text-[#269C6E]'}`}>
                                    {actualShippingFee > 0 ? shippingFeeRowElement.price : translate(language, "default", "OrderWindow/shipping_fee_free")}
                                </span>
                            </div>
                        </div>
                    )}
                    <hr style={{border: '1px dotted #E5E5E5', width: '90%', margin: '0 auto'}} />
                </div>
            }

            {/* Digital Cannabis ID Price row  */}
            {summaryRows["cannabis_id"].shouldShow &&
                <div>
                    <div id='cart-view-breakdown-row-cannabis_id' className='flex justify-between items-start px-5 py-3'>
                        <div className="flex-1">
                            <h4 className="font-roboto text-[15px] font-bold text-[#0D2C54] mb-1">
                                Digitaler Cannabis-Ausweis
                            </h4>
                        </div>
                        <div className="ml-4">
                            <span id='cart-view-breakdown-row-value-cannabis_id' className="font-roboto text-[15px] font-medium text-[#656D79]" translate="no">
                                {getPrice(summaryRows["cannabis_id"].value, cartCurrency.locale, cartCurrency.rate)}
                            </span>
                        </div>
                    </div>
                    <hr style={{border: '1px dotted #E5E5E5', width: '90%', margin: '0 auto'}} />
                </div>
            }

            {/* Coupon discount */}
            {
                summaryRows["coupon"].shouldShow &&
                <div>
                    <div id='cart-view-breakdown-row-coupon' className='flex justify-between items-start px-5 py-3'>
                        <div className="flex-1">
                            <h4 className="font-roboto text-[15px] font-bold text-[#0D2C54] mb-1">
                                {translate(language, "default", "OrderWindow/coupon")}
                            </h4>
                            <div className="flex items-center">
                                <span className="text-[#656D79] text-[13px]">
                                    {translate(language, "default", "OrderWindow/coupon_info")}
                                </span>
                            </div>
                        </div>
                        <div className="ml-4">
                            <span className='font-roboto text-[15px] font-medium text-[#269C6E]' translate="no">
                                {getPrice(summaryRows["coupon"].value, cartCurrency.locale, cartCurrency.rate)}
                            </span>
                        </div>
                    </div>
                </div>
            }

            {/* Applied subscription discount */}
            {
                summaryRows["subscription_discount"].shouldShow &&
                <div>
                    <div id='cart-view-breakdown-row' className='flex justify-between items-start px-5 py-3'>
                        <div className="flex-1">
                            <h4 className="font-roboto text-[15px] font-bold text-[#0D2C54] mb-1">
                                {translate(language, "default", "OrderWindow/treatment_plan_discount")}
                            </h4>
                        </div>
                        <div className="ml-4">
                            <span className='font-roboto text-[15px] font-medium text-[#269C6E]' translate="no">
                                {getPrice(summaryRows["subscription_discount"].value, cartCurrency.locale, cartCurrency.rate)}
                            </span>
                        </div>
                    </div>
                </div>
            }
        </>
    );



    return (
        <div className="w-full">
            {/* Details Header */}
            <div className="flex justify-between items-center px-4 py-3 mb-4 w-full">
                <h3 className="font-roboto text-[13px] font-bold text-[#98A0AF]">
                    {translate(language, "default", "CartViewFAQ/details_header")}
                </h3>
                <div 
                    onClick={() => {
                        setShowCostDetails(!showCostDetails)
                        if (!showCostDetails) {
                            const medicationProduct = cart.products?.find((p: any) => !p.is_addon) ?? cart.products?.[0];
                            trackOncePerSession('custom', 'View Price Breakdown', {
                                plan_fee: Number(cart.care_plus_plan_fee ?? 0) > 0 ? toChargeCurrency(Number(cart.care_plus_plan_fee), cartCurrency.rate, cartCountry) : null,
                                medication_price: toChargeCurrency(medicationProduct?.item_price ?? null, cartCurrency.rate, cartCountry),
                            });
                        }
                    }}
                    className="font-roboto text-[14px] font-medium text-text-link hover:underline cursor-pointer"
                >
                    {showCostDetails ? translate(language, "default", "CartViewFAQ/hide_details") : translate(language, "default", "CartViewFAQ/see_details")}
                </div>

                    {/* Mobile modal */}
                    <PortaledModal 
                        shouldShowModal={animationState !== "" && showCostDetails && isMobile} 
                        hideModal={handleClose}
                        shouldBackgroundClickHideModal={true}
                    >
                        <div 
                            id="cart-view-summary-modal" 
                            className={`w-full max-h-[80vh] bg-white rounded-[30px_30px_0px_0px] shadow-2xl pt-0 flex flex-col overflow-hidden fixed bottom-0 left-0 right-0 ${animationState ? animationState : "hidden"}`}
                        >
                            {/* Mobile top scroller */}
                            <div 
                                className="w-full flex justify-center pt-3 pb-2 md:hidden cursor-pointer"
                                onTouchStart={(e) => {
                                    const startY = e.touches[0].clientY;
                                    e.currentTarget.dataset.startY = startY.toString();
                                }}
                                onTouchMove={(e) => {
                                    const startY = parseFloat(e.currentTarget.dataset.startY || '0');
                                    const currentY = e.touches[0].clientY;
                                    const deltaY = currentY - startY;
                                    
                                    if (deltaY > 50) { // Threshold for swipe down
                                        handleClose();
                                    }
                                }}
                            >
                                <div className="w-24 h-1.5 bg-[#EDEDED] rounded-full" />
                            </div>

                            {/* Scrollable content */}
                            <div id="cart-view-summary-modal-content" className="flex-1 overflow-y-auto custom-scrollbar">
                                {/* Header */}
                                <div 
                                    id="cart-view-summary-modal-header" 
                                    className="relative flex flex-col items-center justify-center pb-2 px-6"
                                >
                                    <div className="w-full flex flex-row items-center justify-center">
                                        <div 
                                            id="cart-view-summary-modal-title" 
                                            className="bg-[#0A9281] max-w-[300px] text-center text-white font-medium text-[14px] px-4 py-2 rounded-b-3xl tracking-wide"
                                        >
                                            {translate(language, "default", "OrderWindow/label")}
                                        </div>
                                    </div>
                                </div>

                                {/* Summary content */}
                                <div className="px-4 md:px-6">
                                    {renderSummaryContent()}
                                </div>

                                {/* Green footer text */}
                                <div className="bg-[#E6F7F4] p-4 pb-10 rounded-lg">
                                    <p className="text-[#0A9281] text-center text-sm font-medium">
                                        {translate(language, "default", "OrderWindow/hidden_fees_disclaimer")}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </PortaledModal>
            </div>

            <div id='cart-view-breakdown' className="bg-white rounded-[16px] shadow-sm w-full">

                {/* Desktop content - hidden on mobile when modal is open */}
                <div className={isMobile || !showCostDetails ? "hidden" : ""}>
                    {renderSummaryContent()}
                </div>
            </div>
        </div>
    );
}

export default CartViewBreakdown;